import { EventPlanOverridesByPlanId } from '../../TeamEvent/components/eventActivityPlanOverrides'

export type ParsedRaidlistParticipant = {
  identity: string
  name: string
  realm: string
  specId: number | null
  classId: number
}

export type RaidPlanCreateNoteState = {
  version: 1
  raidlistRaw: string
  parsedParticipants: ParsedRaidlistParticipant[]
  overrides: EventPlanOverridesByPlanId
  updatedAt: number
}

const storageKey = (planId: number) => `raidPlanCreateNote:${planId}`

const toNumber = (value: string) => Number.parseInt(value, 10)

export const normalizeRaidlistParticipantIdentity = (
  name: string,
  realm: string,
) => `${name.trim().toLowerCase()}-${realm.trim().toLowerCase()}`

const hashString = (value: string) => {
  let hash = 0
  for (let index = 0; index < value.length; index += 1) {
    hash = (hash << 5) - hash + value.charCodeAt(index)
    hash |= 0
  }
  return Math.abs(hash)
}

const toStableId = (prefix: number, identity: string) =>
  prefix + (hashString(identity) % 400000000)

export const getRaidlistParticipantCharacterId = (identity: string) =>
  toStableId(600000000, identity)

export const getRaidlistParticipantId = (identity: string) =>
  toStableId(100000000, identity)

const toParsedRaidlistParticipant = (
  participant: Omit<ParsedRaidlistParticipant, 'identity'> &
    Partial<Pick<ParsedRaidlistParticipant, 'identity'>>,
): ParsedRaidlistParticipant => ({
  identity:
    participant.identity ??
    normalizeRaidlistParticipantIdentity(participant.name, participant.realm),
  name: participant.name,
  realm: participant.realm,
  specId: participant.specId,
  classId: participant.classId,
})

export const parseRaidlistString = (
  raidlistRaw: string,
): ParsedRaidlistParticipant[] | null => {
  const trimmed = raidlistRaw.trim()
  if (!trimmed.toLowerCase().startsWith('raidlist:')) return null
  const body = trimmed.slice('raidlist:'.length).trim().replace(/;$/, '').trim()
  if (!body) return null
  const tokens = body.split(/\s+/).filter(Boolean)
  if (!tokens.length) return null
  const parsed = tokens.map((token) => {
    const [nameAndRealm, specIdRaw, classIdRaw] = token.split('|')
    if (!nameAndRealm || !specIdRaw || !classIdRaw) return null
    const separatorIndex = nameAndRealm.indexOf('-')
    if (separatorIndex <= 0 || separatorIndex >= nameAndRealm.length - 1) {
      return null
    }
    const name = nameAndRealm.slice(0, separatorIndex)
    const realm = nameAndRealm.slice(separatorIndex + 1)
    const parsedSpecId = toNumber(specIdRaw)
    const classId = toNumber(classIdRaw)
    if (!Number.isFinite(parsedSpecId) || !Number.isFinite(classId)) return null
    const specId = parsedSpecId === 0 ? null : parsedSpecId
    return toParsedRaidlistParticipant({ name, realm, specId, classId })
  })
  if (parsed.some((entry) => entry === null)) return null
  return parsed as ParsedRaidlistParticipant[]
}

export const loadRaidPlanCreateNoteState = (
  planId: number,
): RaidPlanCreateNoteState | null => {
  if (typeof window === 'undefined') return null
  const raw = localStorage.getItem(storageKey(planId))
  if (!raw) return null
  try {
    const parsed = JSON.parse(raw) as RaidPlanCreateNoteState
    if (
      parsed?.version !== 1 ||
      typeof parsed.raidlistRaw !== 'string' ||
      !Array.isArray(parsed.parsedParticipants) ||
      typeof parsed.updatedAt !== 'number'
    ) {
      return null
    }
    return {
      version: 1,
      raidlistRaw: parsed.raidlistRaw,
      parsedParticipants: parsed.parsedParticipants.map((participant) =>
        toParsedRaidlistParticipant(
          participant as ParsedRaidlistParticipant &
            Partial<Pick<ParsedRaidlistParticipant, 'identity'>>,
        ),
      ),
      overrides: parsed.overrides ?? {},
      updatedAt: parsed.updatedAt,
    }
  } catch {
    return null
  }
}

export const saveRaidPlanCreateNoteState = (
  planId: number,
  state: Omit<RaidPlanCreateNoteState, 'version' | 'updatedAt'>,
) => {
  if (typeof window === 'undefined') return
  const nextState: RaidPlanCreateNoteState = {
    version: 1,
    raidlistRaw: state.raidlistRaw,
    parsedParticipants: state.parsedParticipants,
    overrides: state.overrides,
    updatedAt: Date.now(),
  }
  localStorage.setItem(storageKey(planId), JSON.stringify(nextState))
}

export const clearRaidPlanCreateNoteState = (planId: number) => {
  if (typeof window === 'undefined') return
  localStorage.removeItem(storageKey(planId))
}
